/**
 * Engagement model: decides when the scene is allowed to swallow the wheel.
 *
 * On a Tilda page the book is one block among many, so a scene that always
 * calls preventDefault on wheel would trap the reader — they could never
 * scroll past the book. The scene therefore only zooms once it has been
 * "taken": the same pointerdown that grabs a page also engages the scene.
 * Clicking outside, pressing Escape, or scrolling the scene out of view
 * releases it again.
 *
 * This is an acknowledged UX compromise, not an ideal: a first-time visitor
 * may not discover the zoom. The hint text softens it. Revisit when the final
 * UI arrives.
 */

const ZOOM_STEP = 0.0016;

/**
 * @param {object} options
 * @param {HTMLElement} options.scene
 * @param {(delta: number) => void} options.onZoom
 * @param {boolean} [options.startEngaged] - true for the standalone preview page
 */
export function createEngagement({ scene, onZoom, startEngaged = false }) {
  let engaged = false;
  let observer = null;

  function setEngaged(next) {
    if (engaged === next) return;
    engaged = next;
    scene.dataset.engaged = String(engaged);
    if (engaged) scene.dataset.hint = 'hidden';
    else delete scene.dataset.hint;
  }

  function onWheel(event) {
    // Registered non-passive so this stays possible, but only ever called when
    // the scene is engaged — otherwise the page scrolls as normal.
    if (!engaged) return;
    event.preventDefault();
    onZoom(-event.deltaY * ZOOM_STEP);
  }

  function onDocumentPointerDown(event) {
    setEngaged(scene.contains(event.target));
  }

  function onKeyDown(event) {
    if (event.key === 'Escape') setEngaged(false);
  }

  scene.dataset.engaged = 'false';
  scene.addEventListener('wheel', onWheel, { passive: false });
  document.addEventListener('pointerdown', onDocumentPointerDown, true);
  document.addEventListener('keydown', onKeyDown);

  if (typeof IntersectionObserver === 'function') {
    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) setEngaged(false);
        }
      },
      { threshold: 0.35 }
    );
    observer.observe(scene);
  }

  if (startEngaged) setEngaged(true);

  return {
    isEngaged() {
      return engaged;
    },

    engage() {
      setEngaged(true);
    },

    destroy() {
      scene.removeEventListener('wheel', onWheel);
      document.removeEventListener('pointerdown', onDocumentPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
      observer?.disconnect();
    }
  };
}
